import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';
import TrainerLayout from './TrainerLayout';
import '../../styles/SurveyList.css';
import { TrainerContext } from '../../context/TrainerContext';
import { FiTrash2 } from 'react-icons/fi';
import SurveyPreviewModal from './SurveyPreviewModal';
import SurveyAssignmentModal from './SurveyAssignmentModal';

const SurveyList = () => {
    const { schoolId } = useParams();
    const navigate = useNavigate();
    const { trainer } = useContext(TrainerContext);

    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Modal states
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
    const [selectedSurvey, setSelectedSurvey] = useState(null);

    // Delete confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [surveyToDelete, setSurveyToDelete] = useState(null);

    useEffect(() => {
        fetchSurveys();
    }, [schoolId]);

    const fetchSurveys = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/v1/survey/school/${schoolId}/all`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });
            setSurveys(response.data);
            setError('');
        } catch (err) {
            console.error('Error fetching surveys:', err);
            setError('Nie udało się pobrać ankiet. Spróbuj ponownie później.');
        } finally {
            setLoading(false);
        }
    };

    const handlePreviewClick = (survey) => {
        setSelectedSurvey(survey);
        setIsPreviewModalOpen(true);
    };

    const handleAssignClick = (survey) => {
        setSelectedSurvey(survey);
        setIsAssignmentModalOpen(true);
    };

    const handleDeleteClick = (e, survey) => {
        e.stopPropagation();
        setSurveyToDelete(survey);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!surveyToDelete) return;

        try {
            await axios.delete(`/api/v1/survey/${surveyToDelete.id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` }
            });
            setShowDeleteConfirm(false);
            setSurveyToDelete(null);
            fetchSurveys();
        } catch (err) {
            console.error('Error deleting survey:', err);
            alert('Nie udało się usunąć ankiety.');
        }
    };

    const handleDeleteFromPreview = () => {
        if (selectedSurvey) {
            setSurveyToDelete(selectedSurvey);
            setIsPreviewModalOpen(false);
            setShowDeleteConfirm(true);
        }
    };

    return (
        <TrainerLayout>
            <div className="sl-container">
                {/* Create Button */}
                <div className="sl-header">
                    <button
                        className="sl-create-btn"
                        onClick={() => navigate(`/survey/create/${schoolId}`)}
                    >
                        Utwórz ankietę
                    </button>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="sl-loading">
                        <p>Ładowanie ankiet...</p>
                    </div>
                ) : error ? (
                    <div className="sl-error">
                        <p>{error}</p>
                        <button onClick={fetchSurveys} className="sl-retry-btn">
                            Spróbuj ponownie
                        </button>
                    </div>
                ) : surveys.length === 0 ? (
                    <div className="sl-empty">
                        <p>Brak ankiet dla tej szkoły.</p>
                        <p>Kliknij "Utwórz ankietę", aby dodać pierwszą ankietę.</p>
                    </div>
                ) : (
                    <div className="sl-grid">
                        {surveys.map(survey => (
                            <div key={survey.id} className="sl-card">
                                <div className="sl-card-header">
                                    <div className="sl-card-info">
                                        <h3 className="sl-card-title">{survey.title}</h3>
                                        <p className="sl-card-count">
                                            Pytania ({Array.isArray(survey.questions) ? survey.questions.length : 0})
                                        </p>
                                    </div>
                                    <button
                                        className="sl-delete-btn"
                                        onClick={(e) => handleDeleteClick(e, survey)}
                                        title="Usuń ankietę"
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>

                                <div className="sl-card-actions">
                                    <button
                                        className="sl-btn-preview"
                                        onClick={() => handlePreviewClick(survey)}
                                    >
                                        Podgląd
                                    </button>
                                    <button
                                        className="sl-btn-assign"
                                        onClick={() => handleAssignClick(survey)}
                                    >
                                        Przypisz
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="sl-confirm-overlay" onClick={() => setShowDeleteConfirm(false)}>
                        <div className="sl-confirm-modal" onClick={(e) => e.stopPropagation()}>
                            <h3>Potwierdź usunięcie</h3>
                            <p>Czy na pewno chcesz usunąć ankietę "{surveyToDelete?.title}"?</p>
                            <p className="sl-confirm-warning">Ta operacja jest nieodwracalna.</p>
                            <div className="sl-confirm-actions">
                                <button
                                    className="sl-confirm-cancel"
                                    onClick={() => setShowDeleteConfirm(false)}
                                >
                                    Anuluj
                                </button>
                                <button
                                    className="sl-confirm-delete"
                                    onClick={confirmDelete}
                                >
                                    Usuń
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Preview Modal */}
                <SurveyPreviewModal
                    isOpen={isPreviewModalOpen}
                    onClose={() => setIsPreviewModalOpen(false)}
                    survey={selectedSurvey}
                    onDelete={handleDeleteFromPreview}
                />

                {/* Assignment Modal */}
                <SurveyAssignmentModal
                    isOpen={isAssignmentModalOpen}
                    onClose={() => {
                        setIsAssignmentModalOpen(false);
                        fetchSurveys();
                    }}
                    survey={selectedSurvey}
                />
            </div>
        </TrainerLayout>
    );
};

export default SurveyList;